import { Injectable, Injector } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { TOKEN_KEY, environment } from 'src/environments/environment'
import { API, User } from '../models'
import { ApiService } from './api.service'
import { ApiUrlService } from './api-url.service'
import { Router } from '@angular/router';
import { NativeStorageProxy } from './native-storage-proxy.service'
import { UserSelectService } from './user-select.service'
import { SettingsService } from './settings.service'
import TwoFactorAuthenticationSetup = API.TwoFactorAuthenticationSetup;
import TwoFactorAuthenticationSetupResponse = API.TwoFactorAuthenticationSetupResponse;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject: BehaviorSubject<User | null>
  public user: Observable<User | null>
  public isAuthenticated: BehaviorSubject<boolean>  = new BehaviorSubject<boolean>(false);
  public forceSingleSignOn: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  private currentAccessToken: string                = '';
  private _scope: BehaviorSubject<Array<string>>    = new BehaviorSubject<Array<string>>([]);
  private settingsService: SettingsService;

  constructor(
    private http: HttpClient,
    private apiUrlService: ApiUrlService,
    private router: Router,
    private nativeStorage: NativeStorageProxy,
    private userSelectService: UserSelectService,
    private injector: Injector,
  ) {
    this.userSubject = new BehaviorSubject(JSON.parse(localStorage.getItem('user')!))
    this.user        = this.userSubject.asObservable()
    this.currentAccessToken
    this.loadToken();
  }

  public get userValue() {
    return this.userSubject.value
  }

  public set userValue(value: User) {
    this.userSubject.next(value)
  }

  public set scope(value: string[]) {
    this._scope.next(value)
  }

  getUser() {
    return this.userValue
  }

  setup2FA(token: string): Observable<TwoFactorAuthenticationSetup> {
    return this.http.get(`${ this.apiUrlService.apiUrl }/users/setup_otp.json?token=${ encodeURIComponent(token) }`).pipe(map((res) => {
      return res as TwoFactorAuthenticationSetup
    }))
  }

  finalize2FA(token: string, otpAttempt: string): Observable<TwoFactorAuthenticationSetupResponse> {
    return this.http.post(`${ this.apiUrlService.apiUrl }/users/finalize_otp_setup.json`, {
      token: encodeURIComponent(token), otp_attempt: otpAttempt,
    }).pipe(map((res) => {
      return { successfully: true } as TwoFactorAuthenticationSetupResponse
    }))
  }

  sso(clientName: string) {
    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/users/sign_in.json`, {
      user:             {
        sso_entity_name: clientName,
      }, redirect_back: '/backend-app',
    }).pipe(map((res) => res))
  }

  login(username: string, password: string, remember_me: boolean, otpAttempt: string) {
    // Add development-only header for login requests
    const options: { headers?: HttpHeaders } = {};
    if (!environment.production) {
      options.headers = new HttpHeaders({
        'X-Localhost-Frontend': 'true'
      });
    }

    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/users/sign_in.json`, {
      user:        {
        email:       username,
        password,
        remember_me: 0
      },
      otp_attempt: otpAttempt
    }, options).pipe(map((res) => {

      if (res.jwt_token) {
        localStorage.setItem(TOKEN_KEY, res.jwt_token)
        res.jwt_token = null
        localStorage.setItem('user', JSON.stringify(res))
        this.userValue = res
        this.isAuthenticated.next(true)
        if (!this.settingsService) {
          this.settingsService = this.injector.get(SettingsService);
        }
        this.settingsService.clearSettingsData()
        return res
      } else if (res.error) {
        // 2FA and other errors
        return res
      } else {
        this.logout()
        return undefined
      }
    }))
  }

  logout() {
    return this.http.get(`${ this.apiUrlService.apiUrl }/users/sign_out.json`).pipe(map(async (res: any) => {
      if (res && res.hasOwnProperty('redirectUrl') && res.redirectUrl) {
        window.location.replace(res.redirectUrl)
      } else {
        this.router.navigate([ '/login' ]);
        await this.clearUserAndCredentials();
        return res
      }
    }, (error) => {
      console.error(error);
    }))
  }

  /**
   * Admin impersonation (backend-app).
   *
   * - backend impersonation: session + jwt
   * - stop impersonation: backend returns a new admin jwt_token + redirect_url
   */
  impersonateUser(userId: number, target?: 'frontend' | 'backend'): Observable<{ jwt_token: string, redirect_url?: string, target?: string, available_targets?: string[] }> {
    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/v2/users/${ userId }/impersonate`, { target }).pipe(map((res) => {
      if (res?.jwt_token) {
        localStorage.setItem(TOKEN_KEY, res.jwt_token)
        this.currentAccessToken = res.jwt_token
        this.isAuthenticated.next(true)
      }
      return res
    }))
  }

  resetUser2FA(userId: number, context: 'clients' | 'organization' = 'organization'): Observable<{ message?: string; otp_required_for_login?: boolean; otp_setup_required?: boolean }> {
    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/api/v1/${ context }/users/${ userId }/reset_otp`, {})
  }

  enableUser2FA(userId: number, context: 'clients' | 'organization' = 'organization'): Observable<{ message?: string; otp_required_for_login?: boolean; otp_setup_required?: boolean }> {
    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/api/v1/${ context }/users/${ userId }/enable_otp`, {})
  }

  disableUser2FA(userId: number, context: 'clients' | 'organization' = 'organization'): Observable<{ message?: string; otp_required_for_login?: boolean; otp_setup_required?: boolean }> {
    return this.http.post<any>(`${ this.apiUrlService.apiUrl }/api/v1/${ context }/users/${ userId }/disable_otp`, {})
  }

  stopImpersonating(): Observable<{ jwt_token: string | null, redirect_url: string }> {
    return this.http.delete<any>(`${ this.apiUrlService.apiUrl }/v2/impersonation`).pipe(map((res) => {
      if (res?.jwt_token) {
        localStorage.setItem(TOKEN_KEY, res.jwt_token)
        this.currentAccessToken = res.jwt_token
        this.isAuthenticated.next(true)
      } else {
        // backend couldn't restore; keep current auth state (caller may force logout)
      }
      return res
    }))
  }

  async clearUserAndCredentials() {
    localStorage.removeItem('user')
    localStorage.removeItem(TOKEN_KEY)
    localStorage.clear();
    this.userSubject.next(null)
    this.isAuthenticated.next(false)
    this.currentAccessToken = ''

    // Preserve the API URL before clearing storage
    const apiUrl = await this.nativeStorage.getItem('api:api-url').catch(() => null);
    await this.nativeStorage.clear();
    // Restore the API URL after clearing
    if (apiUrl) {
      await this.nativeStorage.setItem('api:api-url', apiUrl);
    }

    // Clear cached user and workplace data to prevent data leakage between sessions
    this.userSelectService.clearCache();
    // Clear cached settings data (use lazy injection to avoid circular dependency)
    if (!this.settingsService) {
      this.settingsService = this.injector.get(SettingsService);
    }
    this.settingsService.clearSettingsData();
  }

  async getValidSession(): Promise<any> {
    return this.http.get<any>(this.apiUrlService.apiUrl + '/users/valid_session.json').toPromise().then((res) => {
      if (res?.user) {
        this.userSubject.next(res?.user)
        this.isAuthenticated.next(true)
      } else {
        this.userSubject.next(null)
        this.isAuthenticated.next(false)
      }
      return res
    }).catch(error => {
      console.error('Error', error)
      throw error
    })
  }

  async loadToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      this.currentAccessToken = token;
      this.isAuthenticated.next(true);
    } else {
      this.isAuthenticated.next(false);
    }
  }

}
